import React, { useState, useEffect, useContext, useRef } from 'react';
import { toast } from 'react-toastify';
import { Shield, Edit, CheckCircle, XCircle, Plus, Trash2, Save, AlertCircle, CreditCard, Eye } from 'lucide-react';
import { PermissionContext } from '../../context/PermissionContext';
import { AuthContext } from '../../context/AuthContext';
import { usePermission } from '../../context/PermissionContext';
import roleApi from '../../api/roleApi';
import apiClient from '../../api/apiClient';
import { getMethods } from '../../api/methodApi';

const RoleManager = () => {
  const { rolePermissions, updateRolePermissions } = useContext(PermissionContext);
  const { user } = useContext(AuthContext);
  const { hasPermission } = usePermission();
  
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [localRolePermissions, setLocalRolePermissions] = useState({});
  const [localRolePaymentMethods, setLocalRolePaymentMethods] = useState({});
  const [loading, setLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [editingPaymentMethodsRole, setEditingPaymentMethodsRole] = useState(null);
  const [showAddRoleForm, setShowAddRoleForm] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', displayName: '', description: '' });
  const [authError, setAuthError] = useState(false);
  const [showSetupInfo, setShowSetupInfo] = useState(true);
  const [setupLoading, setSetupLoading] = useState(false);
  const [addPermissionsLoading, setAddPermissionsLoading] = useState(false);
  const [fixCasiPermissionsLoading, setFixCasiPermissionsLoading] = useState(false);
  const [createRolePaymentMethodsLoading, setCreateRolePaymentMethodsLoading] = useState(false);
  
  // Timeout'ları tutmak için ref'ler
  const timeoutRefs = useRef({});
  
  // Initialize roles and permissions
  useEffect(() => {
    fetchRolesAndPermissions();
  }, [user]);
  
  // Roller ve izinleri getir
  const fetchRolesAndPermissions = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setAuthError(false);
      console.log('Roller ve izinler getiriliyor...');
      
      // Rolleri getir
      const rolesResponse = await roleApi.getAllRoles();
      const rolesData = rolesResponse.data || [];
      
      // Rolleri formatla
      const formattedRoles = rolesData.map(role => ({
        id: role.id,
        name: role.name,
        displayName: role.display_name,
        description: role.description,
        isSystemRole: role.is_system_role === 1
      }));
      
      setRoles(formattedRoles);
      console.log('Roller yüklendi:', formattedRoles);
      
      // İzinleri getir
      const permissionsResponse = await roleApi.getAllPermissions();
      const permissionsData = permissionsResponse.data || [];
      
      // Aktif izinleri filtrele
      const filteredPermissions = filterActivePermissions(permissionsData);
      
      setPermissions(filteredPermissions);
      console.log('İzinler yüklendi:', filteredPermissions);
      
      // Boş izin nesnesi oluştur - izinler yalnızca düzenleme sırasında yüklenecek
      const rolePermissions = {};
      
      // BİLGİ: Tüm roller için izinleri başlangıçta yüklemiyoruz, 
      // izinler yalnızca bir rol düzenlendiğinde yüklenecek
      
      // Rol izinlerini güncelle
      setLocalRolePermissions(rolePermissions);
      
      // Ödeme yöntemlerini getir
      try {
        const methodsResponse = await getMethods();
        console.log('Alınan ödeme yöntemleri:', methodsResponse);
        
        // API yanıtının doğruluğunu kontrol et
        if (methodsResponse) {
          if (Array.isArray(methodsResponse)) {
            setPaymentMethods(methodsResponse);
          } else if (methodsResponse.data && Array.isArray(methodsResponse.data)) {
            setPaymentMethods(methodsResponse.data);
          } else {
            console.error('Beklenmeyen ödeme yöntemleri formatı:', methodsResponse);
            setPaymentMethods([]);
          }
        } else {
          console.error('Ödeme yöntemleri alınamadı');
          setPaymentMethods([]);
        }
      } catch (methodError) {
        console.error('Ödeme yöntemleri yüklenirken hata:', methodError);
        setPaymentMethods([]);
      }
      
    } catch (error) {
      console.error('Roller ve izinler yüklenirken hata:', error);
      
      if (error.message && (error.message.includes('401') || error.message.includes('unauthorized'))) {
        setAuthError(true);
        toast.error('Yetkilendirme hatası: Lütfen tekrar giriş yapın');
        
        // Yetkilendirme hatası durumunda setup endpointini kullan
        try {
          const setupRolesResponse = await roleApi.getSetupRoles();
          const setupPermissionsResponse = await roleApi.getSetupPermissions();
          
          const rolesData = setupRolesResponse.data.map(role => ({
            id: role.id,
            name: role.name,
            displayName: role.display_name,
            description: role.description,
            isSystemRole: role.is_system_role === 1
          }));
          
          const permissionsData = setupPermissionsResponse.data.map(perm => ({
            id: perm.id,
            name: perm.name,
            description: perm.description
          }));
          
          setRoles(rolesData);
          setPermissions(filterActivePermissions(permissionsData));
        } catch (setupError) {
          console.error('Setup endpoint\'leri de çalışmıyor:', setupError);
        }
      } else {
        toast.error('Roller ve izinler yüklenirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Veritabanı tablolarını oluştur
  const setupPermissionTables = async () => {
    setSetupLoading(true);
    try {
      // İzin tablolarını oluştur
      const response = await apiClient.post('/setup/create-permission-tables');
      if (response.status === 'success') {
        toast.success('İzin tabloları başarıyla oluşturuldu!');
        
        // Timeout'u temizle ve yeni değeri kaydet
        if (timeoutRefs.current.permissionTablesTimeout) {
          clearTimeout(timeoutRefs.current.permissionTablesTimeout);
        }
        
        timeoutRefs.current.permissionTablesTimeout = setTimeout(() => {
          // Sayfayı 2 saniye sonra yenile
          window.location.reload();
        }, 2000);
      } else {
        toast.error('İzin tabloları oluşturulurken bir hata meydana geldi');
      }
    } catch (error) {
      console.error('İzin tabloları oluşturma hatası:', error);
      toast.error('İzin tabloları oluşturulurken bir hata meydana geldi');
    } finally {
      setSetupLoading(false);
    }
  };
  
  // Yeni izinleri ekle
  const addNewPermissions = async () => {
    setAddPermissionsLoading(true);
    try {
      // Yeni izinleri ekle
      const response = await apiClient.post('/setup/add-new-permissions');
      if (response.status === 'success') {
        toast.success(`Yeni izinler başarıyla eklendi! Toplam izin sayısı: ${response.total_permissions}`);
        
        // Timeout'u temizle ve yeni değeri kaydet
        if (timeoutRefs.current.newPermissionsTimeout) {
          clearTimeout(timeoutRefs.current.newPermissionsTimeout);
        }
        
        timeoutRefs.current.newPermissionsTimeout = setTimeout(() => {
          // Sayfayı 2 saniye sonra yenile
          window.location.reload();
        }, 2000);
      } else {
        toast.error('Yeni izinler eklenirken bir hata meydana geldi');
      }
    } catch (error) {
      console.error('Yeni izinler ekleme hatası:', error);
      toast.error('Yeni izinler eklenirken bir hata meydana geldi');
    } finally {
      setAddPermissionsLoading(false);
    }
  };
  
  // CASI rolünün izinlerini düzelt
  const fixCasiPermissions = async () => {
    setFixCasiPermissionsLoading(true);
    try {
      // CASI rol izinlerini düzelt
      const response = await apiClient.post('/setup/fix-casi-permissions');
      if (response.status === 'success') {
        toast.success('CASI rol izinleri başarıyla düzeltildi!');
        
        // Timeout'u temizle ve yeni değeri kaydet
        if (timeoutRefs.current.casiPermissionsTimeout) {
          clearTimeout(timeoutRefs.current.casiPermissionsTimeout);
        }
        
        timeoutRefs.current.casiPermissionsTimeout = setTimeout(() => {
          // Sayfayı 2 saniye sonra yenile
          window.location.reload();
        }, 2000);
      } else {
        toast.error('CASI rol izinleri düzeltilirken bir hata meydana geldi');
      }
    } catch (error) {
      console.error('CASI rol izinleri düzeltme hatası:', error);
      toast.error('CASI rol izinleri düzeltilirken bir hata meydana geldi');
    } finally {
      setFixCasiPermissionsLoading(false);
    }
  };
  
  // Aktivite log tablosunu oluştur
  const setupActivityLogsTable = async () => {
    setSetupLoading(true);
    try {
      // Aktivite log tablosunu oluştur
      const response = await apiClient.post('/setup/create-activity-logs');
      if (response.status === 'success') {
        toast.success('Aktivite log tablosu başarıyla oluşturuldu!');
      } else {
        toast.error('Aktivite log tablosu oluşturulurken bir hata meydana geldi');
      }
    } catch (error) {
      console.error('Aktivite log tablosu oluşturma hatası:', error);
      toast.error('Aktivite log tablosu oluşturulurken bir hata meydana geldi');
    } finally {
      setSetupLoading(false);
    }
  };
  
  // Rol ile ödeme yöntemleri arasındaki ilişki tablosunu oluştur
  const setupRolePaymentMethodsTable = async () => {
    setCreateRolePaymentMethodsLoading(true);
    try {
      // Rol-ödeme yöntemleri tablosunu oluştur
      const response = await apiClient.post('/setup/create-role-payment-methods');
      if (response.status === 'success') {
        toast.success('Rol-ödeme yöntemleri tablosu başarıyla oluşturuldu!');
        
        // Timeout'u temizle ve yeni değeri kaydet
        if (timeoutRefs.current.rolePaymentMethodsTimeout) {
          clearTimeout(timeoutRefs.current.rolePaymentMethodsTimeout);
        }
        
        timeoutRefs.current.rolePaymentMethodsTimeout = setTimeout(() => {
          // Sayfayı 2 saniye sonra yenile
          window.location.reload();
        }, 2000);
      } else {
        toast.error('Rol-ödeme yöntemleri tablosu oluşturulurken bir hata meydana geldi');
      }
    } catch (error) {
      console.error('Rol-ödeme yöntemleri tablosu oluşturma hatası:', error);
      toast.error('Rol-ödeme yöntemleri tablosu oluşturulurken bir hata meydana geldi');
    } finally {
      setCreateRolePaymentMethodsLoading(false);
    }
  };
  
  // Yönetim paneli görüntüleme iznini ekle
  const addAdminViewPermission = async () => {
    setLoading(true);
    try {
      // API'ye istek gönder
      const response = await apiClient.post('/roles/setup/add-admin-view-permission');
      if (response.status === 'success') {
        toast.success('Yönetim paneli görüntüleme izni başarıyla eklendi!');
        // Sayfayı yenile
        window.location.reload();
      } else {
        toast.error('Yönetim paneli görüntüleme izni eklenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Yönetim paneli izni ekleme hatası:', error);
      toast.error('Yönetim paneli görüntüleme izni eklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  // Kullanılan izinleri filtrele (kullanılmayan izinleri kaldır)
  const filterActivePermissions = (allPermissions) => {
    // Sistemde aktif olarak kullanılan izinler - AKTIVE IZINLERI BURADA GÜNCELLEYIN
    const activePermissionNames = [
      'create_refund',        // İade oluşturma
      'update_refund',        // İade güncelleme
      'delete_refund',        // İade silme
      'view_all_refunds',     // Tüm iadeleri görüntüleme
      'update_refund_status', // İade durumu güncelleme
      'view_payment_methods', // Ödeme yöntemlerini görme
      'view_admin_panel',     // Yönetim paneline erişim
      'manage_users',         // Kullanıcı yönetimi
      'manage_roles',         // Rol yönetimi
      'manage_statuses',      // Durum yönetimi
      'manage_payment_methods', // Ödeme yöntemleri yönetimi
      'manage_refund_reasons'   // İade sebepleri yönetimi
    ];
    
    // Tüm izinleri göstermeyi tercih eden bir developer'sanız aşağıdaki return'ü kullanın:
    // return allPermissions;
    
    // Sadece aktif izinleri filtrele
    return allPermissions.filter(permission => 
      activePermissionNames.includes(permission.name)
    );
  };
  
  // Belirli bir rolün ödeme yöntemlerini getir
  const fetchRolePaymentMethods = async (roleId) => {
    try {
      console.log(`${roleId} ID'li rol için ödeme yöntemlerini getirme isteği gönderiliyor`);
      const response = await roleApi.getRolePaymentMethods(roleId);
      console.log(`${roleId} ID'li rol için ödeme yöntemleri yanıtı:`, response);
      
      // response.data kontrolü
      let methodIds = [];
      
      if (response) {
        if (Array.isArray(response)) {
          methodIds = response.map(method => method.id);
        } else if (response.data && Array.isArray(response.data)) {
          methodIds = response.data.map(method => method.id);
        } else if (response.status === 'success' && Array.isArray(response.data)) {
          methodIds = response.data.map(method => method.id);
        }
      }
      
      console.log(`${roleId} ID'li rol için çıkarılan ödeme yöntemi ID'leri:`, methodIds);
      
      // State güncelleme
      setLocalRolePaymentMethods(prev => ({
        ...prev,
        [roleId]: methodIds
      }));
      
      return methodIds;
    } catch (error) {
      console.error(`Rol ID ${roleId} için ödeme yöntemleri getirilemedi:`, error);
      
      // Hata olsa bile boş dizi ile state'i güncelle
      setLocalRolePaymentMethods(prev => ({
        ...prev,
        [roleId]: []
      }));
      
      return [];
    }
  };
  
  // Ödeme yöntemlerini değiştirmeye başla
  const startEditingPaymentMethods = async (role) => {
    // Check if the user has permission to edit payment methods
    if (!hasPermission('manage_payment_methods') && !hasPermission('view_admin_panel')) {
      toast.error('Bu işlem için ödeme yöntemleri yönetim yetkisi gereklidir');
      return;
    }
    
    // Sistem rolü kontrolü
    if (role.isSystemRole && (role.name === 'admin' || role.name === 'casi')) {
      toast.info('Admin ve CASI rolleri tüm ödeme yöntemlerine erişebilir ve özelleştirilemez');
    }
    
    setLoading(true);
    
    try {
      console.log(`${role.name} rolü için ödeme yöntemlerini düzenleme başlatılıyor`);
      
      // Her zaman sunucudan taze veri al, önbellekten kullanma
      await fetchRolePaymentMethods(role.id);
      
      // Eğer ödeme yöntemleri yoksa veya alınamadıysa boş bir dizi ile başla
      if (!localRolePaymentMethods[role.id]) {
        setLocalRolePaymentMethods(prev => ({
          ...prev,
          [role.id]: []
        }));
      }
      
      // Ödeme yöntemleri boş dizi olsa bile UI'ı açıyoruz
      setEditingPaymentMethodsRole(role);
      console.log(`${role.name} rolü için ödeme yöntemleri düzenleme UI'ı açıldı`);
    } catch (error) {
      console.error('Ödeme yöntemleri yüklenirken hata:', error);
      toast.error('Ödeme yöntemleri yüklenirken bir hata oluştu: ' + error.message);
      
      // Error olsa bile UI'ı açıyoruz, boş durumda gösterilecek
      setEditingPaymentMethodsRole(role);
    } finally {
      setLoading(false);
    }
  };
  
  // Rol ödeme yöntemlerini değiştir
  const togglePaymentMethod = (roleId, methodId) => {
    // Kullanıcının izin kontrolü
    if (!hasPermission('view_admin_panel')) {
      toast.error('Bu işlem için yönetim paneli erişimi gereklidir');
      return;
    }
    
    // Sistem rolleri için kontrol
    const role = roles.find(r => r.id === roleId);
    if (role && role.isSystemRole && (role.name === 'admin' || role.name === 'casi')) {
      toast.warning('Admin ve CASI rolleri tüm ödeme yöntemlerine erişebilir ve bu durum değiştirilemez');
      return;
    }

    // Yerel state'i güncelle
    setLocalRolePaymentMethods(prev => {
      const updatedMethods = { ...prev };
      
      // Rol için boş dizi kontrolü
      if (!updatedMethods[roleId]) {
        updatedMethods[roleId] = [];
      }
      
      // Ödeme yöntemlerini ekle veya kaldır
      if (updatedMethods[roleId].includes(methodId)) {
        // Ödeme yöntemini kaldır
        updatedMethods[roleId] = updatedMethods[roleId].filter(id => id !== methodId);
        console.log(`${methodId} ID'li ödeme yöntemi ${roleId} ID'li rolden kaldırıldı (yerel)`);
      } else {
        // Ödeme yöntemini ekle
        updatedMethods[roleId] = [...updatedMethods[roleId], methodId];
        console.log(`${methodId} ID'li ödeme yöntemi ${roleId} ID'li role eklendi (yerel)`);
      }
      
      return updatedMethods;
    });
  };
  
  // Rol ödeme yöntemlerini kaydet
  const saveRolePaymentMethods = async () => {
    if (!user) {
      toast.error('Değişiklikleri kaydetmek için giriş yapmalısınız');
      return;
    }
    
    // Check if the user has permission to save payment method changes
    if (!hasPermission('view_admin_panel')) {
      toast.error('Bu işlem için yönetim paneli erişimi gereklidir');
      return;
    }
    
    if (!editingPaymentMethodsRole) {
      toast.error('Düzenlenen rol bulunamadı');
      return;
    }
    
    const roleId = editingPaymentMethodsRole.id;
    const roleName = editingPaymentMethodsRole.name;
    
    // Sistem rolleri kontrolü
    if (editingPaymentMethodsRole.isSystemRole && 
        (roleName === 'admin' || roleName === 'casi')) {
      toast.warning('Admin ve CASI rollerinin ödeme yöntemi yetkileri değiştirilemez!');
      
      // Edit modundan çık
      setEditingPaymentMethodsRole(null);
      return;
    }
    
    const methodIds = localRolePaymentMethods[roleId] || [];
    
    setLoading(true);
    
    try {
      // Mevcut ödeme yöntemlerini getir
      const currentRolePaymentMethodsResponse = await roleApi.getRolePaymentMethods(roleId);
      const currentMethodIds = (currentRolePaymentMethodsResponse.data || []).map(m => m.id);
      
      // Eğer değişiklik yoksa gereksiz API çağrısı yapma
      if (JSON.stringify(currentMethodIds.sort()) === JSON.stringify(methodIds.sort())) {
        toast.info('Ödeme yöntemlerinde değişiklik yapılmadı');
        setEditingPaymentMethodsRole(null);
        setLoading(false);
        return;
      }
    
      // API'ye ödeme yöntemlerini gönder
      const response = await roleApi.updateRolePaymentMethods(roleId, methodIds);
      
      if (response && response.status === 'success') {
        // Değişikliğin başarıyla yapıldığını doğrula
        const verifyResponse = await roleApi.getRolePaymentMethods(roleId);
        const verifiedMethodIds = (verifyResponse.data || []).map(m => m.id);
        
        // Onaylama: Ödeme yöntemleri gerçekten güncellenmiş mi?
        const allMethodIdsUpdated = methodIds.every(id => verifiedMethodIds.includes(id)) &&
                                   verifiedMethodIds.length === methodIds.length;
        
        if (allMethodIdsUpdated) {
          toast.success('Rol ödeme yöntemleri başarıyla güncellendi');
          
          // State'i güncelle - localRolePaymentMethods'un güncel kopyasını tut
          const updatedRolePaymentMethods = {
            ...localRolePaymentMethods,
            [roleId]: [...methodIds] // Yeni bir dizi kopyası oluştur
          };
          
          // State'i güncelle
          setLocalRolePaymentMethods(updatedRolePaymentMethods);
        } else {
          // Doğrulama hatası - sunucu farklı bir sonuç döndürdü
          toast.warning('Ödeme yöntemleri güncellendi ancak bazı değişiklikler yapılamadı');
          
          // Sunucudaki gerçek durumu yansıt
          setLocalRolePaymentMethods(prev => ({
            ...prev,
            [roleId]: [...verifiedMethodIds]
          }));
        }
      } else {
        toast.error('Ödeme yöntemleri güncellenirken bir hata oluştu');
        
        // Sunucudan en son durumu al ve yerel state'i güncelle
        const refreshResponse = await roleApi.getRolePaymentMethods(roleId);
        const refreshedIds = (refreshResponse.data || []).map(m => m.id);
        
        setLocalRolePaymentMethods(prev => ({
          ...prev,
          [roleId]: [...refreshedIds]
        }));
      }
      
      setEditingPaymentMethodsRole(null);
    } catch (error) {
      console.error('Ödeme yöntemleri güncelleme hatası:', error);
      toast.error('Ödeme yöntemleri güncellenirken bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
      
      // Hata durumunda rol ödeme yöntemlerini yeniden yükle
      try {
        const response = await roleApi.getRolePaymentMethods(roleId);
        const refreshedIds = (response.data || []).map(m => m.id);
        
        setLocalRolePaymentMethods(prev => ({
          ...prev,
          [roleId]: [...refreshedIds]
        }));
      } catch (e) {
        console.error('Ödeme yöntemlerini yeniden yükleme hatası:', e);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Rol izinlerini değiştir
  const togglePermissionDirect = async (roleName, permissionName) => {
    // Sistem rollerini düzenlemeye izin verme
    const role = roles.find(r => r.name === roleName);
    if (role && role.isSystemRole && (roleName === 'admin')) {
      toast.warning('Admin rolünün yetkileri değiştirilemez!');
      return;
    }

    console.log(`İzin doğrudan değiştiriliyor: ${roleName} - ${permissionName}`);
    console.log('Mevcut izinler:', localRolePermissions[roleName]);
    
    try {
      setLoading(true);
      
      // Yerel izin durumunu güncelle (UI için)
      let updatedPermissions = { ...localRolePermissions };
      
      if (!updatedPermissions[roleName]) {
        updatedPermissions[roleName] = [];
      }
      
      const hasPermission = updatedPermissions[roleName].includes(permissionName);
      
      if (hasPermission) {
        // İzni kaldır
        console.log(`${permissionName} izni kaldırılıyor`);
        updatedPermissions[roleName] = updatedPermissions[roleName].filter(p => p !== permissionName);
      } else {
        // İzni ekle
        console.log(`${permissionName} izni ekleniyor`);
        updatedPermissions[roleName] = [...updatedPermissions[roleName], permissionName];
      }
      
      // Önce yerel state'i güncelle (UI için)
      setLocalRolePermissions(updatedPermissions);
      
      // İzin adını ID'ye dönüştür
      const allPermissionsResponse = await roleApi.getAllPermissions();
      const allPermissions = allPermissionsResponse.data || [];
      
      const permission = allPermissions.find(p => p.name === permissionName);
      if (!permission) {
        toast.error(`İzin bulunamadı: ${permissionName}`);
        setLoading(false);
        return;
      }
      
      // Mevcut rol izinlerini getir
      const rolePermissionsResponse = await roleApi.getRolePermissions(role.id);
      const currentPermissions = (rolePermissionsResponse.data || []).map(p => p.id);
      
      let updatedPermissionIds = [...currentPermissions];
      
      if (hasPermission) {
        // İzni kaldır
        updatedPermissionIds = updatedPermissionIds.filter(id => id !== permission.id);
      } else {
        // İzni ekle
        // Yalnızca zaten yoksa ekle - tekrarı önle
        if (!updatedPermissionIds.includes(permission.id)) {
          updatedPermissionIds.push(permission.id);
        }
      }
      
      // Tekrar eden izinleri önlemek için benzersiz değerleri al
      updatedPermissionIds = [...new Set(updatedPermissionIds)];
      
      console.log(`${roleName} rolü için direkt güncellenen izin ID'leri:`, updatedPermissionIds);
      
      // Rol izinlerini API aracılığıyla güncelle
      const updateResponse = await roleApi.updateRolePermissions(role.id, updatedPermissionIds);
      
      if (updateResponse && updateResponse.status === 'success') {
        const action = hasPermission ? 'kaldırıldı' : 'eklendi';
        
        // Doğrulama: izin gerçekten eklendi/kaldırıldı mı?
        const verifyResponse = await roleApi.getRolePermissions(role.id);
        const updatedPermsList = verifyResponse.data || [];
        const updatedPermsNames = updatedPermsList.map(p => p.name);
        
        const shouldHavePermission = !hasPermission; // Ters çevirdik çünkü yerel durumu zaten güncelledik
        const actuallyHasPermission = updatedPermsNames.includes(permissionName);
        
        if (shouldHavePermission !== actuallyHasPermission) {
          console.error('İzin durumu beklendiği gibi değil!');
          toast.error('İzin durumu güncellenemedi. Sunucu yanıtını kontrol edin.');
          
          // Sunucudan alınan güncel duruma göre yerel state'i güncelle
          setLocalRolePermissions(prev => {
            const fixedPermissions = { ...prev };
            fixedPermissions[roleName] = updatedPermsNames;
            return fixedPermissions;
          });
        } else {
          toast.success(`${permissionName} izni başarıyla ${action}`);
          
          // Yerel ve sunucu durumları eşleşiyor, context'i de güncelle
          updateRolePermissions(localRolePermissions).catch(e => {
            console.error('Context güncellenirken hata:', e);
          });
        }
      } else {
        toast.error('İzin güncellenirken bir hata oluştu');
        
        // Başarısız ise yerel değişikliği geri al
        setLocalRolePermissions(prev => {
          const revertedPermissions = { ...prev };
          
          if (hasPermission) {
            // İzni geri ekle
            revertedPermissions[roleName] = [...revertedPermissions[roleName], permissionName];
          } else {
            // İzni geri çıkar
            revertedPermissions[roleName] = revertedPermissions[roleName].filter(p => p !== permissionName);
          }
          
          return revertedPermissions;
        });
      }
    } catch (error) {
      console.error('İzin değiştirme hatası:', error);
      toast.error('İzin değiştirilirken bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
      
      // Hata durumunda yerel değişikliği geri al
      const originalPermissions = await roleApi.getRolePermissions(role.id);
      const originalNames = (originalPermissions.data || []).map(p => p.name);
      
      setLocalRolePermissions(prev => {
        const fixedPermissions = { ...prev };
        fixedPermissions[roleName] = originalNames;
        return fixedPermissions;
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Rol izinlerini kaydet
  const saveRolePermissions = async () => {
    if (!user) {
      toast.error('Değişiklikleri kaydetmek için giriş yapmalısınız');
      return;
    }
    
    if (!editingRole) {
      toast.error('Düzenlenen rol bulunamadı');
      return;
    }
    
    // Sistem rollerini korumaya al
    const roleToUpdate = roles.find(r => r.name === editingRole);
    if (roleToUpdate && roleToUpdate.isSystemRole && (roleToUpdate.name === 'admin')) {
      toast.warning('Admin rolünün yetkileri değiştirilemez!');
      
      // Admin rolü düzenleniyor ve değişiklikler yapılmış, bunu engelle ve original izinleri göster
      try {
        const adminRoleId = roleToUpdate.id;
        const adminPermsResponse = await roleApi.getRolePermissions(adminRoleId);
        const adminPerms = adminPermsResponse.data || [];
        
        // Admin rolünün gerçek izinlerini yükle
        setLocalRolePermissions(prev => {
          const updatedState = { ...prev };
          updatedState[editingRole] = adminPerms.map(p => p.name);
          return updatedState;
        });
        
        toast.info('Admin rolü yetkileri geri yüklendi, bu rol düzenlenemez');
        setEditingRole(null);
      } catch (error) {
        console.error('Admin rolü izinleri yüklenirken hata:', error);
      }
      return;
    }

    setLoading(true);
    
    try {
      console.log('İzinler güncelleniyor...');
      console.log('Yerel izin durumu:', localRolePermissions);
      
      // Güncellenecek rolü kontrol et
      if (!roleToUpdate) {
        toast.error('Güncellenecek rol bulunamadı');
        setLoading(false);
        return;
      }
      
      console.log(`Güncellenecek rol: ${roleToUpdate.name} (ID: ${roleToUpdate.id})`);
      console.log('Rol için yerel izinler:', localRolePermissions[editingRole]);
      
      // İzinleri doğrudan bu rol için güncelle
      try {
        // İzin adlarından ID'lere dönüştür
        let permissionIds = [];
        
        // Önce tüm izinleri getir
        const allPermissionsResponse = await roleApi.getAllPermissions();
        const allPermissions = allPermissionsResponse.data || [];
        
        // İzinleri bulamazsak hata ver
        if (!allPermissions || !Array.isArray(allPermissions) || allPermissions.length === 0) {
          throw new Error('İzin listesi alınamadı');
        }
        
        let missingPermissionNames = [];
        
        // Tekrarlayan izinleri önlemek için benzersiz izin adlarını al
        const uniquePermissionNames = [...new Set(localRolePermissions[editingRole] || [])];
        
        for (const permName of uniquePermissionNames) {
          const perm = allPermissions.find(p => p.name === permName);
          if (perm) {
            permissionIds.push(perm.id);
          } else {
            console.warn(`'${permName}' izni bulunamadı`);
            missingPermissionNames.push(permName);
          }
        }
        
        // Son kez tekrar eden izin ID'lerini kontrol et
        permissionIds = [...new Set(permissionIds)];
        
        if (missingPermissionNames.length > 0) {
          console.warn(`Bazı izinler bulunamadı ve atlanacak:`, missingPermissionNames);
          toast.warning(`${missingPermissionNames.length} izin sistemde bulunamadı ve atlanacak.`);
        }
        
        console.log(`'${editingRole}' rolü için izin ID'leri (${permissionIds.length}):`, permissionIds);
        
        // API çağrısından önce izinlerin gerçekten güncellendiğini kontrol et
        const currentPermsResponse = await roleApi.getRolePermissions(roleToUpdate.id);
        const currentPerms = currentPermsResponse.data || [];
        const currentPermIds = currentPerms.map(p => p.id);
        
        // Değişiklik yoksa gereksiz API çağrısı yapma
        if (JSON.stringify(currentPermIds.sort()) === JSON.stringify(permissionIds.sort())) {
          toast.info('Hiçbir değişiklik yapılmadı');
          setEditingRole(null);
          setLoading(false);
          return;
        }
        
        // Rol izinlerini API aracılığıyla güncelle
        const updateResponse = await roleApi.updateRolePermissions(roleToUpdate.id, permissionIds);
        console.log('API güncellemesi yanıtı:', updateResponse);
        
        // Başarılı güncellemeyi kontrol et
        if (updateResponse && updateResponse.status === 'success') {
          // İzinleri tekrar kontrol et
          const updatedPermsResponse = await roleApi.getRolePermissions(roleToUpdate.id);
          const updatedPerms = updatedPermsResponse.data || [];
          console.log('Sunucudan alınan güncel izinler:', updatedPerms);
          
          // Dizi olarak izin adlarını al
          const actualPermissionNames = updatedPerms.map(p => p.name);
          
          // Eksik izinleri kontrol et
          const expectedPermissionNames = uniquePermissionNames;
          const missingPermissions = expectedPermissionNames.filter(
            p => !actualPermissionNames.includes(p) && !missingPermissionNames.includes(p)
          );
          
          if (missingPermissions.length > 0) {
            console.warn(`Bazı izinler eklenemedi:`, missingPermissions);
            toast.warning(`${missingPermissions.length} izin eklenemedi. Lütfen yeniden deneyin.`);
            
            // Yerel state'i güncelle - sunucudan gelen gerçek durumu yansıt
            setLocalRolePermissions(prev => {
              const updatedState = { ...prev };
              updatedState[editingRole] = actualPermissionNames;
              return updatedState;
            });
          } else {
            toast.success(`${roleToUpdate.displayName} rolünün yetkileri başarıyla güncellendi`);
          }
          
          // İzin context'inde de güncelle - yerel state'i değil, sunucudan gelen durumu kullan
          const updatedLocalRolePermissions = { ...localRolePermissions };
          updatedLocalRolePermissions[editingRole] = actualPermissionNames;
          
          await updateRolePermissions(updatedLocalRolePermissions);
          setEditingRole(null);
        } else {
          toast.error('Yetkiler güncellenirken bir hata oluştu');
          
          // Sunucudan mevcut durumu al ve yerel state'i güncelle
          const currentPermsResponse = await roleApi.getRolePermissions(roleToUpdate.id);
          const currentPerms = currentPermsResponse.data || [];
          
          setLocalRolePermissions(prev => {
            const revertedPermissions = { ...prev };
            revertedPermissions[editingRole] = currentPerms.map(p => p.name);
            return revertedPermissions;
          });
        }
      } catch (directUpdateError) {
        console.error('Doğrudan API güncellemesi hatası:', directUpdateError);
        toast.error('Yetkiler güncellenirken bir hata oluştu: ' + (directUpdateError.message || 'Bilinmeyen hata'));
        
        // Sunucudan mevcut durumu al ve yerel state'i güncelle
        try {
          const currentPermsResponse = await roleApi.getRolePermissions(roleToUpdate.id);
          const currentPerms = currentPermsResponse.data || [];
          
          setLocalRolePermissions(prev => {
            const revertedPermissions = { ...prev };
            revertedPermissions[editingRole] = currentPerms.map(p => p.name);
            return revertedPermissions;
          });
        } catch (e) {
          console.error('Geri alma sırasında hata:', e);
        }
      }
    } catch (error) {
      console.error('İzin güncelleme hatası:', error);
      toast.error('Yetkiler güncellenirken bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };
  
  // Yeni rol ekle
  const addNewRole = async () => {
    if (!user) {
      toast.error('Yeni rol eklemek için giriş yapmalısınız');
      return;
    }
    
    // Basit validasyon
    if (!newRole.name || !newRole.displayName) {
      toast.error('Rol adı ve görünen adı gereklidir');
      return;
    }
    
    setLoading(true);
    
    try {
      // API'ye yeni rol ekleme isteği gönder
      const response = await roleApi.createRole({
        name: newRole.name,
        display_name: newRole.displayName,
        description: newRole.description
      });
      
      const newRoleData = response.data;
      
      // Rollere ekle
      const newRoleObj = {
        id: newRoleData.id,
        name: newRoleData.name,
        displayName: newRoleData.display_name,
        description: newRoleData.description,
        isSystemRole: newRoleData.is_system_role === 1
      };
      
      setRoles([...roles, newRoleObj]);
      
      // Boş izinlerle başlat ve izin listesini güncelle
      setLocalRolePermissions(prev => {
        const updated = {
          ...prev,
          [newRoleData.name]: []
        };
        
        // PermissionContext'i güncelle
        updateRolePermissions(updated);
        
        return updated;
      });
      
      // Formu sıfırla
      setNewRole({ name: '', displayName: '', description: '' });
      setShowAddRoleForm(false);
      
      toast.success('Yeni rol başarıyla eklendi');
    } catch (error) {
      console.error('Rol eklenirken hata:', error);
      
      if (error.message.includes('401') || error.message.includes('unauthorized')) {
        toast.error('Yetkilendirme hatası: Lütfen tekrar giriş yapın');
      } else {
        toast.error(error.message || 'Rol eklenirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Rol sil
  const deleteRole = async (roleId, roleName) => {
    if (!user) {
      toast.error('Rol silmek için giriş yapmalısınız');
      return;
    }
    
    // Sistem rollerini silmeye izin verme
    const role = roles.find(r => r.name === roleName);
    if (role && role.isSystemRole) {
      toast.warning('Sistem rolleri silinemez!');
      return;
    }
    
    if (!window.confirm(`"${role.displayName}" rolünü silmek istediğinize emin misiniz?`)) {
      return;
    }
    
    setLoading(true);
    
    try {
      // API'ye silme isteği gönder
      await roleApi.deleteRole(roleId);
      
      // Rolleri güncelle
      setRoles(roles.filter(r => r.id !== roleId));
      
      // Rol-izin ilişkilerini güncelle
      const updatedRolePermissions = { ...localRolePermissions };
      delete updatedRolePermissions[roleName];
      
      // Yerel state ve context'i güncelle
      setLocalRolePermissions(updatedRolePermissions);
      updateRolePermissions(updatedRolePermissions);
      
      toast.success('Rol başarıyla silindi');
    } catch (error) {
      console.error('Rol silinirken hata:', error);
      
      if (error.message.includes('401') || error.message.includes('unauthorized')) {
        toast.error('Yetkilendirme hatası: Lütfen tekrar giriş yapın');
      } else {
        toast.error(error.message || 'Rol silinirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Rol ödeme yöntemleri değiştiğinde localStorage'a kaydet
  useEffect(() => {
    if (Object.keys(localRolePaymentMethods).length > 0) {
      try {
        localStorage.setItem('rolePaymentMethods', JSON.stringify(localRolePaymentMethods));
      } catch (error) {
        console.error('Rol ödeme yöntemlerini localStorage\'a kaydederken hata:', error);
      }
    }
  }, [localRolePaymentMethods]);

  // Sayfa yüklendiğinde localStorage'dan ödeme yöntemlerini yükle
  useEffect(() => {
    try {
      const cachedRolePaymentMethods = localStorage.getItem('rolePaymentMethods');
      if (cachedRolePaymentMethods) {
        const parsedData = JSON.parse(cachedRolePaymentMethods);
        setLocalRolePaymentMethods(parsedData);
      }
    } catch (error) {
      console.error('Rol ödeme yöntemlerini localStorage\'dan yüklerken hata:', error);
    }
  }, []);
  
  // Yeni fonksiyon: Belirli bir rol için izinleri yükle
  const loadPermissionsForRole = async (roleName) => {
    try {
      console.log(`${roleName} rolü için izinleri yükleniyor...`);
      const role = roles.find(r => r.name === roleName);
      
      if (!role) {
        console.error(`${roleName} rolü bulunamadı!`);
        return false;
      }
      
      setLoading(true);
      
      // Rol izinlerini getir
      const permissionsResult = await roleApi.getRolePermissions(role.id);
      const permissions = permissionsResult.data || [];
      
      // İzin isimlerini diziye ekle
      const updatedRolePermissions = { ...localRolePermissions };
      updatedRolePermissions[roleName] = permissions.map(p => p.name);
      
      // Yerel state'i güncelle
      setLocalRolePermissions(updatedRolePermissions);
      console.log(`${roleName} rolü için izinler yüklendi:`, updatedRolePermissions[roleName]);
      
      return true;
    } catch (err) {
      console.error(`${roleName} rolü izinleri yüklenirken hata:`, err);
      toast.error(`${roleName} rolü izinleri yüklenirken hata oluştu`);
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Komponent unmount olduğunda tüm timeout'ları temizle
  useEffect(() => {
    return () => {
      // Tüm timeout'ları temizle
      Object.values(timeoutRefs.current).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
    };
  }, []);
  
  // Eğer yetkilendirme hatası varsa bilgi mesajı göster
  if (authError) {
    return (
      <div className="p-6 bg-orange-50 text-center rounded-lg border border-orange-200">
        <AlertCircle size={48} className="mx-auto text-orange-500 mb-4" />
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Yetkilendirme Hatası</h2>
        <p className="text-gray-600 mb-4">
          Rol yönetimi sayfasına erişmek için giriş yapmalısınız.
        </p>
        <p className="text-sm text-gray-500">
          Yalnızca izinleri görüntüleyebilirsiniz, ancak değişiklik yapamazsınız.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Rol Yönetimi</h2>
        {user && (
          <button
            onClick={() => setShowAddRoleForm(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <Plus size={16} className="inline-block mr-1" />
            Yeni Rol Ekle
          </button>
        )}
      </div>
      
      {/* Giriş yapılmamışsa bilgi mesajı */}
      {!user && (
        <div className="p-4 bg-yellow-50 text-yellow-700 rounded-md border border-yellow-200 mb-4">
          <AlertCircle size={16} className="inline-block mr-2" />
          Rol değişikliği yapmak için giriş yapmalısınız. Şu an sadece görüntüleme modundasınız.
        </div>
      )}
      
      {/* Yeni Rol Ekleme Formu */}
      {showAddRoleForm && (
        <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mb-4">
          <h3 className="text-lg font-medium text-gray-800 mb-3">Yeni Rol Ekle</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rol Adı
              </label>
              <input
                type="text"
                value={newRole.name}
                onChange={(e) => setNewRole({...newRole, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Yetki Adi"
              />
              <p className="mt-1 text-xs text-gray-500">Sistem içi kullanılacak kod adı</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Görünen Ad
              </label>
              <input
                type="text"
                value={newRole.displayName}
                onChange={(e) => setNewRole({...newRole, displayName: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Yetki Adi"
              />
              <p className="mt-1 text-xs text-gray-500">Kullanıcılara gösterilecek ad</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Açıklama
              </label>
              <input
                type="text"
                value={newRole.description}
                onChange={(e) => setNewRole({...newRole, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Acıklama"
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowAddRoleForm(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              İptal
            </button>
            <button
              onClick={addNewRole}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              disabled={loading || !user}
            >
              {loading ? 'Ekleniyor...' : 'Ekle'}
            </button>
          </div>
        </div>
      )}
      
      {/* Roller Tablosu */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rol Adı
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Açıklama
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sistem Rolü
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                İşlemler
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {roles.map((role) => (
              <tr key={role.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Shield size={16} className="text-primary-500 mr-2" />
                    <div>
                      <div className="font-medium text-gray-900">{role.displayName}</div>
                      <div className="text-sm text-gray-500">{role.name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {role.description}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  {role.isSystemRole ? (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      Sistem
                    </span>
                  ) : (
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      Özel
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {user && (
                    <>
                      {editingRole === role.name ? (
                        <button 
                          onClick={saveRolePermissions}
                          disabled={loading}
                          className="text-green-600 hover:text-green-900 mr-2"
                          title="İzinleri Kaydet"
                        >
                          <Save size={16} className="inline-block" />
                          <span className="sr-only">Kaydet</span>
                        </button>
                      ) : (
                        <button 
                          onClick={async () => {
                            await loadPermissionsForRole(role.name);
                            setEditingRole(role.name);
                          }}
                          className="text-blue-600 hover:text-blue-900 mr-2"
                          title="İzinleri Düzenle"
                        >
                          <Edit size={16} className="inline-block" />
                          <span className="sr-only">Düzenle</span>
                        </button>
                      )}
                      
                      {editingPaymentMethodsRole && editingPaymentMethodsRole.id === role.id ? (
                        <button 
                          onClick={saveRolePaymentMethods}
                          disabled={loading}
                          className="text-green-600 hover:text-green-900 mr-2"
                          title="Ödeme Yöntemlerini Kaydet"
                        >
                          <Save size={16} className="inline-block" />
                          <span className="sr-only">Ödeme Yöntemlerini Kaydet</span>
                        </button>
                      ) : hasPermission('view_admin_panel') && (
                        <button 
                          onClick={() => startEditingPaymentMethods(role)}
                          className="text-purple-600 hover:text-purple-900 mr-2"
                          title="Ödeme Yöntemlerini Düzenle"
                        >
                          <CreditCard size={16} className="inline-block" />
                          <span className="sr-only">Ödeme Yöntemlerini Düzenle</span>
                        </button>
                      )}
                      
                      {!role.isSystemRole && (
                        <button 
                          onClick={() => deleteRole(role.id, role.name)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 size={16} className="inline-block" />
                          <span className="sr-only">Sil</span>
                        </button>
                      )}
                    </>
                  )}
                  
                  {!user && (
                    <button 
                      onClick={async () => {
                        await loadPermissionsForRole(role.name);
                        setEditingRole(role.name);
                      }}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Eye size={16} className="inline-block" />
                      <span className="sr-only">Görüntüle</span>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Rol İzinleri Bölümü */}
      {editingRole && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-800">
              <Shield size={18} className="inline-block mr-1 text-primary-500" />
              "{roles.find(r => r.name === editingRole)?.displayName}" Rol Yetkileri
            </h3>
            <button
              onClick={() => setEditingRole(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              İptal
            </button>
          </div>
          
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-3 bg-gray-50 border-b">
              <p className="text-sm text-gray-600">
                Bu ekranda yalnızca aktif olarak kullanılan izinler listelenmektedir.
              </p>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Yetki Adı
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Açıklama
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filterActivePermissions(permissions).map((permission) => (
                  <tr key={permission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {permission.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {permission.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center items-center">
                        <button
                          onClick={() => user && togglePermissionDirect(editingRole, permission.name)}
                          className={`transition-colors duration-150 ${
                            localRolePermissions[editingRole]?.includes(permission.name)
                              ? 'bg-green-500 hover:bg-green-600' 
                              : 'bg-red-500 hover:bg-red-600'
                          } w-6 h-6 rounded flex items-center justify-center`}
                          title={
                            localRolePermissions[editingRole]?.includes(permission.name)
                              ? `${permission.name} izni var - Tıklayarak kaldırın`
                              : `${permission.name} izni yok - Tıklayarak ekleyin`
                          }
                        >
                          {localRolePermissions[editingRole]?.includes(permission.name) ? (
                            <CheckCircle size={14} className="text-white" />
                          ) : (
                            <XCircle size={14} className="text-white" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {user && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={saveRolePermissions}
                disabled={loading}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-primary-300 disabled:cursor-not-allowed"
              >
                {loading ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Rol Ödeme Yöntemleri Bölümü */}
      {editingPaymentMethodsRole && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-800">
              <CreditCard size={18} className="inline-block mr-1 text-primary-500" />
              "{editingPaymentMethodsRole.displayName}" Rolü İçin Ödeme Yöntemleri
            </h3>
            <button
              onClick={() => setEditingPaymentMethodsRole(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              İptal
            </button>
          </div>
          
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 bg-gray-50 border-b">
              <p className="text-sm text-gray-600">
                Bu rol için hangi ödeme yöntemlerinin görüntülenebileceğini ve işlenebileceğini seçin. 
                Seçili ödeme yöntemlerine sahip iadeleri görebilecek ve yönetebilecekler.
              </p>
            </div>
            
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {paymentMethods && paymentMethods.length > 0 ? (
                paymentMethods.map(method => (
                  <div key={method.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`method-${method.id}`}
                      checked={(localRolePaymentMethods[editingPaymentMethodsRole.id] || []).includes(method.id)}
                      onChange={() => togglePaymentMethod(editingPaymentMethodsRole.id, method.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      disabled={(editingPaymentMethodsRole.name === 'admin' || editingPaymentMethodsRole.name === 'casi') && editingPaymentMethodsRole.isSystemRole}
                    />
                    <label htmlFor={`method-${method.id}`} className="ml-2 block text-sm text-gray-900">
                      {method.name}
                      {method.code && <span className="text-xs text-gray-500 block">{method.code}</span>}
                    </label>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-4">
                  <p className="text-gray-500 mb-2">Henüz ödeme yöntemi bulunmuyor veya yüklenemiyor.</p>
                  <button 
                    onClick={() => getMethods().then(data => {
                      if (Array.isArray(data)) setPaymentMethods(data);
                      else if (data && Array.isArray(data.data)) setPaymentMethods(data.data);
                      else console.error('Beklenmeyen veri formatı:', data);
                    }).catch(err => console.error('Yenileme hatası:', err))}
                    className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                  >
                    Yeniden Yükle
                  </button>
                </div>
              )}
            </div>
            
            <div className="p-4 bg-gray-50 border-t">
              <div className="flex justify-end">
                <button
                  onClick={saveRolePaymentMethods}
                  disabled={loading || (editingPaymentMethodsRole.name === 'admin' || editingPaymentMethodsRole.name === 'casi') && editingPaymentMethodsRole.isSystemRole}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-primary-300 disabled:cursor-not-allowed"
                >
                  {loading ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                </button>
              </div>
              
              {((editingPaymentMethodsRole.name === 'admin' || editingPaymentMethodsRole.name === 'casi') && editingPaymentMethodsRole.isSystemRole) && (
                <p className="text-sm text-yellow-600 mt-2">
                  <AlertCircle size={14} className="inline-block mr-1" />
                  Admin ve CASI rolleri tüm ödeme yöntemlerine erişebilir ve bu durum değiştirilemez.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManager; 